import { observer } from 'mobx-react';
import { useMemo, useRef } from "react";
import { useMedia, useStore } from 'shared/hooks';
import { PPDWidgetBaseHeader } from '../PPDWidgetBaseHeader';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Grid, Pagination, Navigation } from 'swiper';
import { UserDashboardWidgetModel } from "shared/models";
import { currency, number } from "shared/utilities";

import { PPDWidgetBonusBadge } from '../PPDWidgetBonusBadge';
import { PPDWidgetBonusModal } from '../PPDWidgetBonusModal';
import "swiper/css/grid";
import 'swiper/css/pagination';
import './styles.scss';

import iconArrow from './assets/chevron-right.svg';
import classnames from 'classnames';

type PropsType = {
    indexWidget?: number;
    widget: UserDashboardWidgetModel
}

export const PPDWidgetGrid = observer(({ widget, indexWidget }: PropsType) => {
    const store = useStore(() => ({
        isOpenBonusModal: false,
        isOpenContent: false,
    }));

    const { isMobileOrTablet } = useMedia();

    const item = useMemo(() => {
        if (widget.items.length === 0) {
            return null;
        }

        return widget.items[0];
    }, [widget]);

    const groupedItems = useMemo(() => {
        const arr: typeof widget.items[] = [];
        for (let i = 0; i < widget.items.length; i += 6) {
            arr.push(widget.items.slice(i, i + 6));
        }
        return arr;
    }, [widget.items]);

    if (!item === null) {
        return null;
    }

    return (
        <>
            <div className={`ppd-widget ppd-widget--grid ppd-widget-${indexWidget}`}>
                <PPDWidgetBaseHeader
                    name={widget.name || '-'}
                    isOpen={store.isOpenContent}
                    onToggle={() => store.toggle("isOpenContent")}
                />

                {(!isMobileOrTablet || store.isOpenContent) && (
                    <div className="ppd-widget-grid">
                        {!!widget?.bonuses?.items?.length && (
                            <div className="ppd-widget-grid__bonus-badge">
                                <PPDWidgetBonusBadge
                                    isActive={widget?.bonuses?.status_id === 'received'}
                                    onClick={() => store.set("isOpenBonusModal", true)}
                                />
                            </div>
                        )}

                        <div className="ppd-widget-grid__wrapper">
                            {
                                widget.items.length > 6 ? (
                                    <>
                                        <Swiper
                                            enabled={widget.items.length > 6}
                                            spaceBetween={8}
                                            slidesPerView={1}
                                            pagination={{
                                                clickable: true,
                                            }}
                                            navigation={{
                                                nextEl: `.ppd-widget-${indexWidget} .swiper-button-next-custom`,
                                                prevEl: `.ppd-widget-${indexWidget} .swiper-button-prev-custom`,
                                            }}
                                            modules={[Navigation, Grid, Pagination]}
                                            className="ppd-widget-grid__slider"
                                        >
                                            {groupedItems.map((group, gIndex) => (
                                                <SwiperSlide key={gIndex}>
                                                    <div className={`ppd-widget-grid__list ${group.length >= 3? 'ppd-widget-grid__list--left': ''}`}>
                                                        {group.map((item, index) => {
                                                            const isSignLabel = item.valueSign;
                                                            return (
                                                                <div
                                                                    key={index}
                                                                    className={`ppd-widget-grid-cell ppd-widget-grid__item ${isSignLabel ? 'ppd-widget-grid-cell--big' : ''}`}
                                                                >
                                                                    <div className="ppd-widget-grid-cell__label">
                                                                        <span>{item.valueHint}</span>
                                                                        {item.valueSign && (
                                                                            <span className="ppd-widget-grid-cell__label-sign">{item.valueSign}</span>
                                                                        )}
                                                                    </div>
                                                                    <div
                                                                        className="ppd-widget-grid-cell__value"
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: currency(number(item.value.amount), '₽', true),
                                                                        }}
                                                                    />
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </SwiperSlide>
                                            ))}
                                        </Swiper>
                                        <div className="swiper-button-prev-custom" style={{backgroundImage: `url(${iconArrow})`}}>
                                        </div>
                                        <div className="swiper-button-next-custom" style={{backgroundImage: `url(${iconArrow})`}}>
                                        </div>
                                    </>
                                ) : (
                                    <div
                                        className={classnames('ppd-widget-grid__list', {
                                            'ppd-widget-grid__list--grow': widget.items.length === 5,
                                            'ppd-widget-grid__list--two-columns': widget.items.length === 4,
                                        })}
                                    >
                                        {widget.items.map((item, index) => {
                                            const isSignLabel = widget.items.find(item => item.valueSign);

                                            return (<div key={index} className={`ppd-widget-grid__item ppd-widget-grid-cell ${isSignLabel ? 'ppd-widget-grid-cell--big' : ''}`}>
                                                <div className="ppd-widget-grid-cell__label">
                                                    <span>{item.valueHint}</span>
                                                    {item.valueSign ? <span className="ppd-widget-grid-cell__label-sign">{item.valueSign}</span> : ''}
                                                </div>
                                                <div className="ppd-widget-grid-cell__value" dangerouslySetInnerHTML={{ __html: currency(number(item.value.amount), '₽', true) }}></div>
                                            </div>)
                                        })}
                                    </div>
                                )
                            }
                        </div>
                    </div>
                )}
            </div>

            <PPDWidgetBonusModal
                isOpen={store.isOpenBonusModal}
                onClose={() => store.set("isOpenBonusModal", false)}
                groups={widget.bonuses?.groups || []}
                items={widget.bonuses?.items || []}
                total={widget.bonuses?.total}
                labels={widget.labels || {}}
            />
        </>
    );
});
